import { useCallback, useEffect, useState } from "react";
import { api, fmtErr, fmtDate, reviewServiceLabel } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Star, Eye, EyeOff, Check, X, MessageSquareReply, Send, ShieldCheck } from "lucide-react";

const MODERATE_MSGS = {
  publish: "Recensione pubblicata.",
  reject: "Recensione rifiutata ed eliminata.",
  hide: "Recensione nascosta (resta in archivio).",
  feature: "Recensione in evidenza nella home.",
  unfeature: "Rimossa dall'evidenza (resta in archivio).",
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
    api.get("/admin/feedback").then((r) => setFeedback(r.data)).catch(() => setFeedback([]));
  }, []);

  useEffect(load, [load]);

  const moderate = async (r, action) => {
    try {
      await api.put(`/admin/reviews/${r.id}/moderate`, { action });
      toast.success(MODERATE_MSGS[action] || "Fatto.");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa recensione?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Recensione eliminata.");
    load();
  };

  const sendReply = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}/reply`, { reply: replyText });
      toast.success(replyText.trim() ? "Risposta pubblicata sotto la recensione." : "Risposta rimossa.");
      setReplyOpen(null);
      setReplyText("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-reviews-section">
      <div className="mb-10" data-testid="admin-feedback-section">
        <h3 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400/80 mb-3">Feedback delle consultazioni</h3>
        {feedback.length === 0 ? (
          <p className="text-[11px] text-purple-300/50" data-testid="admin-feedback-empty">Nessun feedback ancora: le valutazioni con stelle delle consultazioni appariranno qui.</p>
        ) : (
          <div className="space-y-2" data-testid="admin-feedback-list">
            {feedback.map((f) => (
              <div key={f.id} className="mystic-card px-4 py-3" data-testid={`admin-feedback-${f.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-0.5" data-testid={`admin-feedback-stars-${f.id}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= f.stars ? "text-amber-400 fill-amber-400" : "text-purple-300/30"}`} />
                    ))}
                  </span>
                  <span className="text-[10px] text-purple-300/50">{f.method} · {f.user} · {fmtDate(f.at)}</span>
                </div>
                {f.question && <p className="text-[11px] text-purple-300/60 italic mt-1">«{f.question}»</p>}
                {f.comment && <p className="text-xs text-purple-100/80 mt-1.5">{f.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mb-6">
        <h2 className="font-display text-2xl text-amber-100">Recensioni</h2>
        <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex items-start gap-2.5" data-testid="admin-reviews-real-note">
          <ShieldCheck className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
          <p className="text-xs text-emerald-100/80 leading-relaxed">
            Qui compaiono <strong>solo le recensioni reali</strong> inviate dagli utenti. Non è possibile creare recensioni manuali.
          </p>
        </div>
      </div>

      {reviews !== null && reviews.some((r) => r.status === "pending") && (
        <div className="mb-8" data-testid="admin-reviews-pending">
          <p className="eyebrow mb-4">Da approvare</p>
          <div className="space-y-3">
            {reviews.filter((r) => r.status === "pending").map((r) => (
              <div key={r.id} className="mystic-card px-6 py-5 border-amber-500/40" data-testid={`admin-review-pending-${r.id}`}>
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <p className="text-amber-100 font-medium">{r.author}</p>
                  <span className="flex gap-0.5">
                    {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                  </span>
                  {r.service && (
                    <span className="text-[10px] uppercase tracking-wider text-purple-300/60 border border-[#4a2175] rounded-full px-2 py-0.5">{reviewServiceLabel(r.service)}</span>
                  )}
                </div>
                {(r.email || r.created_at) && (
                  <p className="text-[11px] text-purple-300/50 mb-2" data-testid={`admin-review-meta-${r.id}`}>
                    {r.email && <span className="text-purple-200/70">{r.email}</span>}{r.email && r.created_at && " · "}{r.created_at && fmtDate(r.created_at)}
                  </p>
                )}
                <p className="text-sm text-purple-200/70 italic mb-4">“{r.text}”</p>
                <div className="flex gap-3">
                  <button onClick={() => moderate(r, "publish")} data-testid={`admin-review-publish-${r.id}`} className="gold-btn px-4 py-1.5 text-xs flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Pubblica
                  </button>
                  <button onClick={() => moderate(r, "reject")} data-testid={`admin-review-reject-${r.id}`} className="text-xs text-red-300/80 border border-red-500/40 rounded-full px-4 py-1.5 hover:bg-red-500/10 transition-colors flex items-center gap-1.5">
                    <X className="w-3.5 h-3.5" /> Rifiuta
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {reviews === null ? null : reviews.length === 0 ? (
        <div className="mystic-card p-10 text-center" data-testid="admin-reviews-empty">
          <Star className="w-8 h-8 text-amber-400/50 mx-auto mb-3" />
          <p className="text-purple-200/60 text-sm">Nessuna recensione. Qui compariranno le recensioni reali inviate dagli utenti.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.filter((r) => r.status !== "pending").map((r) => (
            <div key={r.id} className={`mystic-card px-6 py-5 ${!r.published ? "opacity-50" : ""}`} data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                    <p className="text-amber-100 font-medium">{r.author}</p>
                    <span className="flex gap-0.5">
                      {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                    </span>
                    {r.service && <span className="text-[10px] uppercase tracking-wider text-purple-300/60 border border-[#4a2175] rounded-full px-2 py-0.5">{reviewServiceLabel(r.service)}</span>}
                    {!r.published && <span className="text-[10px] uppercase tracking-wider text-purple-400/60 border border-[#4a2175] rounded-full px-2 py-0.5">Nascosta</span>}
                    {r.featured && <span className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-500/50 rounded-full px-2 py-0.5" data-testid={`admin-review-featured-badge-${r.id}`}>In evidenza</span>}
                  </div>
                  {(r.email || r.created_at) && (
                    <p className="text-[11px] text-purple-300/50 mb-2" data-testid={`admin-review-meta-${r.id}`}>
                      {r.email && <span className="text-purple-200/70">{r.email}</span>}{r.email && r.created_at && " · "}{r.created_at && fmtDate(r.created_at)}
                    </p>
                  )}
                  <p className="text-sm text-purple-200/70 italic">“{r.text}”</p>
                  {r.reply && (
                    <div className="mt-3 ml-3 pl-4 border-l-2 border-amber-500/50" data-testid={`admin-review-reply-${r.id}`}>
                      <p className="text-[10px] uppercase tracking-widest text-amber-300/80">Risposta di Virginia</p>
                      <p className="text-purple-100/80 text-sm mt-1">{r.reply}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => moderate(r, r.featured ? "unfeature" : "feature")} data-testid={`admin-review-featured-${r.id}`} className={`p-2 transition-colors ${r.featured ? "text-amber-300" : "text-purple-300/70 hover:text-amber-300"}`} title={r.featured ? "Togli dall'evidenza" : "Metti in evidenza nella home"}>
                    <Star className={`w-4 h-4 ${r.featured ? "fill-amber-300" : ""}`} />
                  </button>
                  <button onClick={() => moderate(r, r.published ? "hide" : "publish")} data-testid={`admin-review-toggle-${r.id}`} className="p-2 text-purple-300/70 hover:text-amber-300 transition-colors" title={r.published ? "Nascondi" : "Pubblica"}>
                    {r.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => { setReplyOpen(replyOpen === r.id ? null : r.id); setReplyText(r.reply || ""); }}
                    data-testid={`admin-review-reply-btn-${r.id}`}
                    className="p-2 text-purple-300/70 hover:text-amber-300 transition-colors"
                    title={r.reply ? "Modifica risposta" : "Rispondi"}
                  >
                    <MessageSquareReply className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(r.id)} data-testid={`admin-review-delete-${r.id}`} className="p-2 text-purple-300/70 hover:text-red-300 transition-colors" title="Elimina">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {replyOpen === r.id && (
                <div className="mt-3 flex gap-2" data-testid={`admin-review-reply-form-${r.id}`}>
                  <input
                    data-testid={`admin-review-reply-input-${r.id}`}
                    value={replyText}
                    maxLength={1000}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendReply(r)}
                    placeholder="Scrivi la risposta di Virginia (visibile sotto la recensione)…"
                    className="input-dark flex-1"
                  />
                  <button data-testid={`admin-review-reply-send-${r.id}`} onClick={() => sendReply(r)} className="gold-btn w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
